import { useCallback, useEffect, useRef, useState } from 'react';

const RUN_MS = 4500;
const STOP_MS = 500;
const SCRATCH_MS = 650;

const unwrapDelta = (from: number, to: number) => {
  let delta = to - from;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
};

const pointerAngle = (el: HTMLElement, clientX: number, clientY: number) => {
  const rect = el.getBoundingClientRect();
  return Math.atan2(
    clientY - (rect.top + rect.height / 2),
    clientX - (rect.left + rect.width / 2),
  );
};

/** Default DJ rest: left of the label, on the vinyl that shows beside the form. */
const demoScratchPoint = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect();
  return {
    x: rect.left + rect.width * 0.32,
    y: rect.top + rect.height * 0.5,
  };
};

const usePlatterPlay = () => {
  const discRef = useRef<HTMLDivElement>(null);
  const busyRef = useRef(false);
  const draggingRef = useRef(false);
  const hoveringRef = useRef(false);
  const lastAngleRef = useRef(0);
  const rotateRef = useRef(0);

  const [stopped, setStopped] = useState(false);
  const [scratching, setScratching] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [hand, setHand] = useState<{ x: number; y: number } | null>(null);
  const [dragRotate, setDragRotate] = useState(0);
  const [demoGrab, setDemoGrab] = useState(false);
  const [orbitOrigin, setOrbitOrigin] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const demoGrabRef = useRef(false);

  busyRef.current = dragging || hoveringRef.current;
  demoGrabRef.current = demoGrab;

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motion.matches) return;

    let cancelled = false;
    let timer = 0;

    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        timer = window.setTimeout(resolve, ms);
      });

    const showDemoHand = () => {
      if (!discRef.current || busyRef.current) return;
      const rect = discRef.current.getBoundingClientRect();
      setOrbitOrigin({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
      setHand(demoScratchPoint(discRef.current));
      setDemoGrab(true);
    };

    const hideDemoHand = () => {
      setDemoGrab(false);
      setOrbitOrigin(null);
      if (!hoveringRef.current && !draggingRef.current) setHand(null);
    };

    const loop = async () => {
      while (!cancelled) {
        setScratching(false);
        hideDemoHand();
        if (!busyRef.current) setStopped(false);
        await wait(RUN_MS);
        if (cancelled) break;
        if (busyRef.current) continue;
        setStopped(true);
        showDemoHand();
        await wait(STOP_MS);
        if (cancelled || busyRef.current) {
          hideDemoHand();
          if (!busyRef.current) setStopped(false);
          continue;
        }
        setScratching(true);
        await wait(SCRATCH_MS);
        hideDemoHand();
      }
    };

    loop();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      setHand({ x: event.clientX, y: event.clientY });
      if (!draggingRef.current || !discRef.current) return;

      const next = pointerAngle(discRef.current, event.clientX, event.clientY);
      rotateRef.current +=
        unwrapDelta(lastAngleRef.current, next) * (180 / Math.PI);
      lastAngleRef.current = next;
      setDragRotate(rotateRef.current);
    },
    [],
  );

  const onPointerEnter = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      hoveringRef.current = true;
      setHand({ x: event.clientX, y: event.clientY });
    },
    [],
  );

  const onPointerLeave = useCallback(() => {
    hoveringRef.current = false;
    if (!draggingRef.current && !demoGrabRef.current) setHand(null);
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!discRef.current || event.button !== 0) return;
      event.preventDefault();
      hoveringRef.current = true;
      discRef.current.setPointerCapture(event.pointerId);
      draggingRef.current = true;
      lastAngleRef.current = pointerAngle(
        discRef.current,
        event.clientX,
        event.clientY,
      );
      setDragging(true);
      setStopped(true);
      setScratching(false);
      setHand({ x: event.clientX, y: event.clientY });
    },
    [],
  );

  const endDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setDragging(false);
    setStopped(false);
    if (discRef.current?.hasPointerCapture(event.pointerId)) {
      discRef.current.releasePointerCapture(event.pointerId);
    }
    const under = document.elementFromPoint(event.clientX, event.clientY);
    if (!under || !discRef.current?.contains(under)) {
      hoveringRef.current = false;
      setHand(null);
    }
  }, []);

  return {
    discRef,
    stopped: stopped || dragging,
    scratching: scratching && !dragging,
    dragging,
    hand,
    demoGrab,
    orbitOrigin,
    dragRotate,
    onPointerMove,
    onPointerEnter,
    onPointerLeave,
    onPointerDown,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  };
};

export default usePlatterPlay;
